import { useState, useEffect } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import UserAPI from "../api/UserApi";
import { showToast } from "../utils/showToast";
import { isMemberConsentField, overrideFieldKeyError, parseFieldValue } from "../utils/userFields";
import { variantURL, fallbackToOriginal } from "../utils/imageVariant";

const UserDetailsPopup = ({ user, onClose }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [attendedEvents, setAttendedEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [editingError, setEditingError] = useState(null);
  const [fieldSaving, setFieldSaving] = useState(false);
  const [fieldSaved, setFieldSaved] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(null);
  const [eventsCollapsed, setEventsCollapsed] = useState(false);
  const [deletedFields, setDeletedFields] = useState(new Set());
  const [overrideFields, setOverrideFields] = useState(() => {
    const initial = [];
    if (user?.customPrice != null && Number(user.customPrice) > 0) {
      initial.push({ id: "customPrice", key: "customPrice", value: String(user.customPrice), isNew: false, saving: false, saved: false });
    }
    return initial;
  });

  const uid = user?.id || user?.userID;

  const addOverrideField = () => {
    setOverrideFields((prev) => [...prev, { id: Date.now(), key: "", value: "", isNew: true, saving: false, saved: false }]);
  };

  const updateOverrideField = (id, changes) => {
    setOverrideFields((prev) => prev.map((f) => (f.id === id ? { ...f, ...changes } : f)));
  };

  const saveOverrideField = async (id) => {
    const field = overrideFields.find((f) => f.id === id);
    if (!field || !field.key.trim()) return;
    const newId = field.key.trim();
    // Say why a key is refused right under the row. UserAPI.setUserField would
    // throw on a consent field anyway, but that only reached a toast.
    const keyError = overrideFieldKeyError(newId);
    if (keyError) {
      updateOverrideField(id, { error: keyError });
      return;
    }
    updateOverrideField(id, { saving: true, error: null });
    try {
      await UserAPI.setUserField(uid, newId, parseFieldValue(field.value));
      setOverrideFields((prev) =>
        prev.map((f) => (f.id === id ? { ...f, id: newId, saving: false, saved: true, isNew: false } : f))
      );
      setTimeout(() => updateOverrideField(newId, { saved: false }), 2000);
    } catch (error) {
      // Surface the failure instead of swallowing it — a silent catch here left
      // the field looking unsaved with no reason, so an admin couldn't tell a
      // write failed (e.g. a rules rejection) from a value that never changed.
      console.error("Failed to save override field:", error);
      updateOverrideField(id, { saving: false });
      showToast(`Couldn't save "${newId}": ${error?.message || "unknown error"}`, { type: "error", autoClose: 4000 });
    }
  };

  const startEditingField = (field, currentValue) => {
    const isComplex = typeof currentValue === 'object' && currentValue !== null;
    setEditingField(field);
    setEditingValue(isComplex ? JSON.stringify(currentValue, null, 2) : String(currentValue ?? ""));
    setEditingError(null);
    setFieldSaved(null);
  };

  const cancelEditingField = () => {
    setEditingField(null);
    setEditingValue("");
  };

  const deleteField = async (field) => {
    try {
      await UserAPI.removeUserField(uid, field);
      setDeletedFields((prev) => new Set([...prev, field]));
      setConfirmingDelete(null);
    } catch (error) {
      // Don't mark the field deleted in the UI if the write failed.
      console.error("Failed to delete field:", error);
      showToast(`Couldn't delete "${field}": ${error?.message || "unknown error"}`, { type: "error", autoClose: 4000 });
    }
  };

  const saveEditingField = async (isJson) => {
    if (!editingField) return;
    setFieldSaving(true);
    setEditingError(null);
    try {
      let parsed;
      if (isJson) {
        try {
          parsed = JSON.parse(editingValue);
        } catch {
          setEditingError("Invalid JSON");
          setFieldSaving(false);
          return;
        }
      } else {
        parsed = parseFieldValue(editingValue);
      }
      await UserAPI.setUserField(uid, editingField, parsed);
      setFieldSaved(editingField);
      setEditingField(null);
      setTimeout(() => setFieldSaved(null), 2000);
    } catch (error) {
      // Keep the editor open and show why the save failed instead of letting
      // the rejection go unhandled while the admin assumes it saved.
      console.error("Failed to save field:", error);
      setEditingError(error?.message || "Save failed");
    } finally {
      setFieldSaving(false);
    }
  };

  const removeOverrideField = async (id) => {
    const field = overrideFields.find((f) => f.id === id);
    if (!field) return;
    if (!field.isNew && field.key) {
      try {
        await UserAPI.removeUserField(uid, field.key);
      } catch (error) {
        // Leave the field in place if the delete write failed.
        console.error("Failed to remove override field:", error);
        showToast(`Couldn't remove "${field.key}": ${error?.message || "unknown error"}`, { type: "error", autoClose: 4000 });
        return;
      }
    }
    setOverrideFields((prev) => prev.filter((f) => f.id !== id));
  };

  useEffect(() => {
    if (!user?.id && !user?.userID) return;

    const fetchUserEvents = async () => {
      setEventsLoading(true);
      try {
        const uid = user.id || user.userID;
        const eventsRef = collection(db, "events");

        // Query attended (movers/sitters) and RSVPed (rsvpMovers/rsvpSitters)
        const [moversSnap, sittersSnap, rsvpMoversSnap, rsvpSittersSnap] = await Promise.all([
          getDocs(query(eventsRef, where("movers", "array-contains", uid))),
          getDocs(query(eventsRef, where("sitters", "array-contains", uid))),
          getDocs(query(eventsRef, where("rsvpMovers", "array-contains", uid))),
          getDocs(query(eventsRef, where("rsvpSitters", "array-contains", uid))),
        ]);

        const attendedIds = new Set();
        [...moversSnap.docs, ...sittersSnap.docs].forEach((doc) => attendedIds.add(doc.id));

        const rsvpIds = new Set();
        [...rsvpMoversSnap.docs, ...rsvpSittersSnap.docs].forEach((doc) => rsvpIds.add(doc.id));

        const eventsMap = new Map();
        const allDocs = [...moversSnap.docs, ...sittersSnap.docs, ...rsvpMoversSnap.docs, ...rsvpSittersSnap.docs];
        allDocs.forEach((doc) => {
          if (!eventsMap.has(doc.id)) {
            const data = doc.data();
            eventsMap.set(doc.id, {
              id: doc.id,
              name: data.name || "Untitled Event",
              date: data.date?.toDate?.() || null,
              hostname: data.hostname || "",
              isCompleted: data.isCompleted || false,
              category: data.category || "",
              status: attendedIds.has(doc.id) ? "attended" : "rsvped",
            });
          }
        });

        const events = Array.from(eventsMap.values()).sort(
          (a, b) => (b.date || 0) - (a.date || 0)
        );
        setAttendedEvents(events);
      } catch (error) {
        console.error("Error fetching user events:", error);
      } finally {
        setEventsLoading(false);
      }
    };

    fetchUserEvents();
  }, [user]);

  if (!user) return null;

  return (
    <>
      <div
        className="fixed inset-y-0 right-0 max-w-md w-full bg-white dark:bg-gray-800 shadow-2xl overflow-y-auto z-50"
      >
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{user.name || "User Details"}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 text-2xl font-light"
          >
            ×
          </button>
        </div>

        <div className="p-6">
          {user.photoURL && (
            <div className="mb-6 relative rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 group">
              <img
                src={variantURL(user.photoURL, 512)}
                onError={fallbackToOriginal(user.photoURL)}
                alt="Background blur"
                className="absolute inset-0 w-full h-64 object-cover blur-2xl opacity-50"
              />
              <img
                src={variantURL(user.photoURL, 512)}
                onError={fallbackToOriginal(user.photoURL)}
                alt="User avatar"
                className="relative w-full h-64 object-contain rounded-lg"
              />
              <button
                onClick={() => setIsFullscreen(true)}
                className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-lg opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                title="View fullscreen"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="w-5 h-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
                  />
                </svg>
              </button>
            </div>
          )}

        {/* Overrides Section */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 tracking-wide mb-3">
            OVERRIDES
          </h3>
          <div className="space-y-2">
            {overrideFields.map((field) => (
              <div key={field.id}>
              <div className="flex gap-2 items-center">
                {field.isNew ? (
                  <>
                    <input
                      list="override-field-options"
                      type="text"
                      value={field.key}
                      onChange={(e) => updateOverrideField(field.id, { key: e.target.value, error: null })}
                      placeholder="field"
                      className="w-28 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm font-mono bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                    />
                    <datalist id="override-field-options">
                      <option value="customPrice" />
                    </datalist>
                  </>
                ) : (
                  <span className="w-28 text-sm text-gray-500 dark:text-gray-400 font-mono truncate" title={field.key}>
                    {field.key}
                  </span>
                )}
                <input
                  type="text"
                  value={field.value}
                  onChange={(e) => updateOverrideField(field.id, { value: e.target.value })}
                  placeholder="value"
                  className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                />
                <button
                  onClick={() => saveOverrideField(field.id)}
                  disabled={field.saving}
                  className="text-sm bg-gray-900 dark:bg-gray-700 text-white px-3 py-1 rounded disabled:opacity-50 whitespace-nowrap"
                >
                  {field.saved ? "Saved!" : field.saving ? "..." : "Save"}
                </button>
                <button
                  onClick={() => removeOverrideField(field.id)}
                  className="text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 text-xl leading-none"
                >
                  ×
                </button>
              </div>
              {field.error && (
                <p role="alert" className="text-xs text-red-500 dark:text-red-400 mt-1">
                  {field.error}
                </p>
              )}
              </div>
            ))}
          </div>
          <button
            onClick={addOverrideField}
            className="mt-3 text-sm text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 flex items-center gap-1"
          >
            <span className="text-base leading-none">+</span> Add field
          </button>
        </div>

        {/* Events Attended Section */}
        <div className="mb-6">
          <button
            onClick={() => setEventsCollapsed((c) => !c)}
            className="flex items-center gap-1.5 w-full text-left mb-3 group"
          >
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 tracking-wide">
              EVENTS
            </h3>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className={`w-3.5 h-3.5 text-gray-400 dark:text-gray-500 transition-transform ${eventsCollapsed ? "-rotate-90" : ""}`}
            >
              <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
            </svg>
          </button>
          {!eventsCollapsed && eventsLoading ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">Loading events...</p>
          ) : !eventsCollapsed && attendedEvents.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">No events found.</p>
          ) : !eventsCollapsed ? (
            <div className="space-y-2">
              {attendedEvents.map((event) => (
                <div
                  key={event.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  onClick={() => window.open(`/events/${event.id}/edit`, "_blank")}
                >
                  <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                    {event.name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2 flex-wrap">
                    {event.date && (
                      <span>
                        {event.date.toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </span>
                    )}
                    {event.hostname && <span>· {event.hostname}</span>}
                    {event.category && (
                      <span className="bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 rounded text-[10px] uppercase">
                        {event.category}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-1.5 mt-1.5">
                    <span
                      className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded ${
                        event.status === "attended"
                          ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                          : "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300"
                      }`}
                    >
                      {event.status === "attended" ? "Attended" : "RSVPed"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <div className="space-y-4">
          {Object.keys(user).map((field) => {
            if (deletedFields.has(field)) return null;
            if (user[field] && typeof user[field] === 'string' && user[field].startsWith("https://")) {
              return null;
            }

            const value = user[field];
            const isObject = typeof value === 'object' && value !== null;
            const isArray = Array.isArray(value);
            const isComplex = isObject || isArray;
            const isEditing = editingField === field;
            const justSaved = fieldSaved === field;
            const isConfirmingDelete = confirmingDelete === field;
            const isConsentField = isMemberConsentField(field);

            return (
              <div key={field} className="border-b border-gray-100 dark:border-gray-800 pb-3">
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide mb-1 flex items-center gap-2 group/label">
                  {field}
                  {isConsentField && (
                    <span
                      className="text-[10px] font-normal normal-case text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-700 rounded px-1.5 py-0.5"
                      title="Member consent setting; only the member can change it"
                    >
                      member consent
                    </span>
                  )}
                  {!isConsentField && !isEditing && !isConfirmingDelete && (
                    <>
                      <button
                        onClick={() => startEditingField(field, value)}
                        className="opacity-0 group-hover/label:opacity-100 transition-opacity text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 text-xs px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setConfirmingDelete(field)}
                        className="opacity-0 group-hover/label:opacity-100 transition-opacity text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 text-xs px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-700 hover:border-red-300 dark:hover:border-red-700"
                      >
                        Delete
                      </button>
                    </>
                  )}
                  {isConfirmingDelete && (
                    <span className="flex items-center gap-1.5">
                      <span className="text-red-500 dark:text-red-400 text-xs">Delete?</span>
                      <button
                        onClick={() => deleteField(field)}
                        className="text-xs bg-red-500 text-white px-2 py-0.5 rounded"
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => setConfirmingDelete(null)}
                        className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 px-1.5 py-0.5"
                      >
                        No
                      </button>
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-900 dark:text-gray-100">
                  {isEditing ? (
                    <div className="mt-1">
                      {isComplex ? (
                        <textarea
                          autoFocus
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          rows={Math.min(Math.max(editingValue.split("\n").length, 3), 12)}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs font-mono resize-y bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                        />
                      ) : (
                        <input
                          autoFocus
                          type="text"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveEditingField(false);
                            if (e.key === "Escape") cancelEditingField();
                          }}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                        />
                      )}
                      {editingError && (
                        <p className="text-xs text-red-500 dark:text-red-400 mt-1">{editingError}</p>
                      )}
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => saveEditingField(isComplex)}
                          disabled={fieldSaving}
                          className="text-sm bg-gray-900 dark:bg-gray-700 text-white px-3 py-1 rounded disabled:opacity-50"
                        >
                          {fieldSaving ? "..." : "Save"}
                        </button>
                        <button
                          onClick={cancelEditingField}
                          className="text-sm text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 px-2 py-1"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : isArray ? (
                    <div className="mt-1 space-y-1">
                      {justSaved ? (
                        <span className="text-green-600 dark:text-green-400 text-xs">Saved</span>
                      ) : value.map((item, idx) => (
                        <div key={idx} className="pl-2 text-gray-700 dark:text-gray-200">
                          • {typeof item === 'object' ? JSON.stringify(item) : item}
                        </div>
                      ))}
                    </div>
                  ) : isObject ? (
                    <pre className="text-xs bg-gray-50 dark:bg-gray-800 p-2 rounded mt-1 overflow-x-auto">
                      {justSaved ? <span className="text-green-600 dark:text-green-400">Saved</span> : JSON.stringify(value, null, 2)}
                    </pre>
                  ) : (
                    <div className="break-words">
                      {justSaved ? (
                        <span className="text-green-600 dark:text-green-400">{String(value)}</span>
                      ) : (
                        String(value)
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </div>
      </div>

      {isFullscreen && (
        <div
          className="fixed inset-0 bg-black/95 z-[60] flex items-center justify-center p-4"
          onClick={() => setIsFullscreen(false)}
        >
          <button
            onClick={() => setIsFullscreen(false)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 text-4xl font-light"
            title="Close fullscreen"
          >
            ×
          </button>
          <img
            src={user.photoURL}
            alt="User avatar fullscreen"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default UserDetailsPopup;
